import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { openInvoice, openConfirmation } from '@/lib/invoice';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Circle, Truck, Package, Factory, FileDown, FileText } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';

const OrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const { format } = useCurrency();
  const { t, tModel } = useLanguage();

  useEffect(() => { api.get(`/orders/${id}`).then(({ data }) => setOrder(data)); }, [id]);

  if (!order) return <div className="p-10 text-white/60">{t('orders.loading', 'Loading order...')}</div>;

  const STEPS = [
    { key: 'placed', label: t('orders.placed', 'Placed'), icon: Package },
    { key: 'in_production', label: t('orders.inProduction', 'In Production'), icon: Factory },
    { key: 'shipped', label: t('orders.shipped', 'Shipped'), icon: Truck },
    { key: 'delivered', label: t('orders.delivered', 'Delivered'), icon: CheckCircle2 },
  ];
  const statusLabel = (s) => {
    const map = {
      placed: t('orders.placed', 'Placed'),
      in_production: t('orders.inProduction', 'In Production'),
      shipped: t('orders.shipped', 'Shipped'),
      delivered: t('orders.delivered', 'Delivered'),
      cancelled: t('orders.cancelled', 'Cancelled'),
    };
    return map[s] || (s || '').replace('_', ' ');
  };
  const currentIdx = STEPS.findIndex(s => s.key === order.status);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">{order.order_number}</h1>
          <p className="text-white/60 text-sm">{t('orders.placedOn', 'Placed')} {new Date(order.created_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-white/[0.06] border-white/10 text-white/80 capitalize" data-testid="order-status-badge">{statusLabel(order.status)}</Badge>
          {order.invoice_number && !order.is_voided && (
            <Badge className={order.paid_at ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-amber-500/15 text-amber-200 border-amber-500/30'} data-testid="order-payment-badge">
              {order.paid_at ? t('orders.paid', 'Paid') : t('orders.awaitingPayment', 'Awaiting payment')}
            </Badge>
          )}
          {order.is_voided && (
            <Badge className="bg-rose-500/20 text-rose-200 border-rose-500/40" data-testid="order-voided-badge">
              {t('orders.voided', 'Voided')}
            </Badge>
          )}
          <Button size="sm" onClick={async () => { await openConfirmation(order.id); }} data-testid="order-confirmation-button" className="jw-btn-gloss-ghost h-8">
            <FileText className="w-4 h-4 mr-1" /> {t('orders.confirmation', 'Confirmation')}
          </Button>
          {order.invoice_number && (
            <Button size="sm" onClick={async () => { await openInvoice(order.id); }} data-testid="order-invoice-button" className="jw-btn-gloss-ghost h-8">
              <FileDown className="w-4 h-4 mr-1" /> {t('orders.invoice', 'Invoice (SAR)')}
            </Button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <Card className="jw-card p-6 mt-6" data-testid="order-status-timeline">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {STEPS.map((s, idx) => {
            const done = idx <= currentIdx && order.status !== 'cancelled';
            return (
              <div key={s.key} className="flex flex-col items-center text-center">
                <div className={`w-12 h-12 rounded-full grid place-items-center border ${done ? 'jw-gradient-bg text-white border-white/20' : 'bg-white/[0.04] text-white/50 border-white/10'}`}>
                  {done ? <CheckCircle2 className="w-6 h-6" /> : <s.icon className="w-6 h-6" />}
                </div>
                <p className={`mt-2 text-sm ${done ? 'text-white' : 'text-white/50'}`}>{s.label}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-6 border-t border-white/10 pt-4">
          <p className="text-xs text-white/60 mb-2">{t('orders.history', 'History')}</p>
          <ul className="space-y-2">
            {(order.tracking || []).map((tr, i) => (
              <li key={i} className="text-sm flex gap-3 text-white/80"><Circle className="w-2 h-2 mt-2 fill-white/60 text-white/60" /> <span><b>{statusLabel(tr.status)}</b> — {tr.note} · <span className="text-white/50">{new Date(tr.at).toLocaleString()}</span></span></li>
            ))}
          </ul>
        </div>
      </Card>

      {/* Items */}
      <Card className="jw-card p-5 mt-6">
        <h3 className="font-semibold text-white">{t('orders.items', 'Items')}</h3>
        <div className="mt-4 space-y-3">
          {order.items.map((it, i) => (
            <div key={i} className="flex gap-3 items-center">
              <img src={it.product?.image} alt="" className="w-14 h-14 rounded-md object-cover bg-[color:var(--jw-navy-950)]" />
              <div className="flex-1">
                <p className="text-white text-sm">{it.product?.name}</p>
                <p className="text-white/50 text-xs">{tModel(it.variant)} · {t('orders.qty', 'Qty')} {it.quantity}</p>
              </div>
              <span className="text-white text-sm">{format(it.price * it.quantity)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-white/10 pt-3 text-sm space-y-1">
          <div className="flex justify-between text-white/70"><span>{t('cart.subtotal', 'Subtotal')}</span><span>{format(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-emerald-300"><span>{t('orders.discount', 'Discount')}</span><span>- {format(order.discount)}</span></div>}
          <div className="flex justify-between text-white/70"><span>{t('cart.shipping', 'Shipping')}</span><span>{order.shipping === 0 ? t('cart.free', 'Free') : format(order.shipping)}</span></div>
          <div className="flex justify-between text-white font-semibold pt-2 border-t border-white/10 mt-2"><span>{t('cart.total', 'Total')}</span><span>{format(order.total)}</span></div>
        </div>
      </Card>

      <Card className="jw-card p-5 mt-6">
        <h3 className="font-semibold text-white">{t('orders.shippingTo', 'Shipping to')}</h3>
        <p className="text-white/70 text-sm mt-2">{order.address?.name}<br />{order.address?.line1}, {order.address?.city}, {order.address?.state} {order.address?.zip}, {order.address?.country}</p>
      </Card>
    </div>
  );
};

export default OrderDetail;
